import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';
import { useAuthStore } from '@/stores/authStore';
import { checkRateLimit } from '@/lib/validation';
import { readFunctionError, BillingError } from '@/lib/billingErrors';
import type { UserRole } from '@/types/database';

// ============================================
// TYPES
// ============================================

export interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: UserRole;
  company_name: string | null;
  subscription_status: string;
  subscription_plan: string;
  max_properties: number;
  language: string;
  created_at: string;
  updated_at: string;
  properties_count: number;
  units_count: number;
  bookings_count: number;
}

export interface AdminUserDetail extends AdminUser {
  total_revenue: number;
  last_booking_at: string | null;
  current_period_end: string | null;
}

export interface AdminStatistics {
  total_users: number;
  total_landlords: number;
  total_properties: number;
  total_units: number;
  total_bookings: number;
  active_bookings: number;
  total_revenue: number;
  monthly_revenue: number;
  users_this_month: number;
  bookings_this_month: number;
}

export interface RevenueTrend {
  month: string;
  revenue: number;
  bookings_count: number;
}

export interface SubscriptionStats {
  plan: string;
  status: string;
  count: number;
}

export interface ExpiringSoonRow {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  subscription_plan: string;
  subscription_status: string;
  subscription_type: string | null;
  expires_at: string;        // ISO date
  expires_source: 'current_period_end' | 'beta_expires_at';
  days_remaining: number;
  language: string | null;
}

export interface SendPaymentLinkInput {
  userId: string;
  mode?: 'auto' | 'portal' | 'checkout';
  plan?: 'earlyAdopter' | 'starter' | 'pro' | 'business';
  interval?: 'month' | 'year';
  message?: string;
}

export interface SendPaymentLinkResult {
  ok: true;
  mode: 'portal' | 'checkout';
  url: string;
  email_dispatched: boolean;
  target_email: string;
  tier_id?: string;
  interval?: string;
  expires_at?: string;
  /** The host has more active units than the chosen plan allows (not blocking). */
  active_units?: number;
  max_units?: number;
  /** Edge flag: active_units > max_units (warn, not blocked). */
  units_over_limit?: boolean;
  warning?: string | boolean | null;
  units_warning?: boolean;
}

// ============================================
// SERVER-SIDE ADMIN VERIFICATION
// ============================================

/**
 * Verify admin status on server side
 * This prevents frontend-only checks which can be bypassed
 */
async function verifyAdminServer(userId: string): Promise<boolean> {
  const { data, error } = await getSupabase().rpc('is_admin', { user_id: userId });

  if (error) {
    console.error('Admin verification failed:', error);
    return false;
  }

  return data === true;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to verify admin status (server-side)
 */
export function useAdminVerification() {
  const { userId, isReady } = useAuthReady();
  const { isAdmin: isAdminFrontend } = useAuthStore();

  return useQuery({
    queryKey: ['admin-verification', userId],
    queryFn: async () => {
      if (!userId) return false;

      // Double check - frontend AND server
      if (!isAdminFrontend()) return false;

      return verifyAdminServer(userId);
    },
    enabled: isReady,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });
}

/**
 * Hook to get all users (admin only)
 */
export function useAdminUsers() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');

      // Rate limit check (client-side, server also checks)
      const rateLimit = checkRateLimit(`admin-users-${userId}`, 30, 60000);
      if (!rateLimit.allowed) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }

      const { data, error } = await getSupabase().rpc('admin_get_all_users', {
        p_admin_id: userId,
      });

      if (error) {
        console.error('Failed to fetch users:', error);
        throw new Error(error.message);
      }

      return data as AdminUser[];
    },
    enabled: isReady,
  });
}

/**
 * Hook to get single user details (admin only)
 */
export function useAdminUser(targetUserId: string | undefined) {
  const { userId: adminUserId, isReady } = useAuthReady();

  return useQuery({
    queryKey: ['admin-user', targetUserId],
    queryFn: async () => {
      if (!adminUserId || !targetUserId) throw new Error('Missing parameters');

      const { data, error } = await getSupabase().rpc('admin_get_user', {
        p_admin_id: adminUserId,
        p_user_id: targetUserId,
      });

      if (error) {
        console.error('Failed to fetch user:', error);
        throw new Error(error.message);
      }

      return (data as AdminUserDetail[])?.[0] || null;
    },
    enabled: isReady && !!targetUserId,
  });
}

/**
 * Hook to get user's properties (admin view)
 */
export function useAdminUserProperties(targetUserId: string | undefined) {
  const { isReady } = useAuthReady();
  const { data: isAdmin } = useAdminVerification();

  return useQuery({
    queryKey: ['admin-user-properties', targetUserId],
    queryFn: async () => {
      if (!targetUserId || !isAdmin) throw new Error('Unauthorized');

      const { data, error } = await getSupabase()
        .from('properties')
        .select(`
          *,
          units:units(count)
        `)
        .eq('landlord_id', targetUserId)
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message);
      return data;
    },
    enabled: isReady && !!targetUserId && isAdmin === true,
  });
}

/**
 * Hook to get user's bookings (admin view)
 */
export function useAdminUserBookings(targetUserId: string | undefined) {
  const { isReady } = useAuthReady();
  const { data: isAdmin } = useAdminVerification();

  return useQuery({
    queryKey: ['admin-user-bookings', targetUserId],
    queryFn: async () => {
      if (!targetUserId || !isAdmin) throw new Error('Unauthorized');

      const { data, error } = await getSupabase()
        .from('bookings')
        .select(`
          *,
          unit:units(name, property:properties(name))
        `)
        .eq('landlord_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw new Error(error.message);
      return data;
    },
    enabled: isReady && !!targetUserId && isAdmin === true,
  });
}

/**
 * Hook to update user role (admin only)
 */
export function useUpdateUserRole() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      newRole,
    }: {
      userId: string;
      newRole: UserRole;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      // Rate limit check
      const rateLimit = checkRateLimit(`admin-update-role-${user.id}`, 10, 60000);
      if (!rateLimit.allowed) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }

      const { data, error } = await getSupabase().rpc('admin_update_user_role', {
        p_admin_id: user.id,
        p_user_id: userId,
        p_new_role: newRole,
      });

      if (error) {
        console.error('Failed to update role:', error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
  });
}

/**
 * Hook to update subscription (admin only)
 */
export function useUpdateSubscription() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      status,
      plan,
      maxProperties,
    }: {
      userId: string;
      status: string;
      plan: string;
      maxProperties?: number;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      // Rate limit check
      const rateLimit = checkRateLimit(`admin-update-sub-${user.id}`, 10, 60000);
      if (!rateLimit.allowed) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }

      const { data, error } = await getSupabase().rpc('admin_update_subscription', {
        p_admin_id: user.id,
        p_user_id: userId,
        p_status: status,
        p_plan: plan,
        p_max_properties: maxProperties || null,
      });

      if (error) {
        console.error('Failed to update subscription:', error);
        // Keep the server text (e.g. HAS_STRIPE_SUBSCRIPTION) — the UI maps it.
        throw new Error([error.message, error.details, error.hint].filter(Boolean).join(' | '));
      }

      return data;
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-statistics'] });
    },
  });
}

/**
 * Hook to gift / extend a paid subscription for an existing user (admin only).
 * Bypasses Stripe — used for "give Pro for 6 months free".
 *   - durationMonths = 0 → lifetime
 *   - extend = true     → stack onto current_period_end if still in future
 */
export function useGiftSubscription() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      plan,
      durationMonths,
      extend = false,
    }: {
      userId: string;
      plan: 'earlyAdopter' | 'starter' | 'pro' | 'business' | 'enterprise';
      durationMonths: number;
      extend?: boolean;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      const rateLimit = checkRateLimit(`admin-gift-sub-${user.id}`, 10, 60000);
      if (!rateLimit.allowed) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }

      const { data, error } = await getSupabase().rpc('admin_gift_subscription', {
        p_admin_id: user.id,
        p_user_id: userId,
        p_plan: plan,
        p_duration_months: durationMonths,
        p_extend: extend,
      });

      if (error) {
        console.error('Failed to gift subscription:', error);
        // Keep the server text (e.g. HAS_STRIPE_SUBSCRIPTION) — the UI maps it.
        throw new Error([error.message, error.details, error.hint].filter(Boolean).join(' | '));
      }

      return data;
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-statistics'] });
    },
  });
}

/**
 * Hook to get system statistics (admin only)
 */
export function useAdminStatistics() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: ['admin-statistics'],
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await getSupabase().rpc('admin_get_statistics', {
        p_admin_id: userId,
      });

      if (error) {
        console.error('Failed to fetch statistics:', error);
        throw new Error(error.message);
      }

      return (data as AdminStatistics[])?.[0] || null;
    },
    enabled: isReady,
    staleTime: 60 * 1000, // Refresh every minute
  });
}

/**
 * Hook to get revenue trend (admin only)
 */
export function useAdminRevenueTrend(months: number = 12) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: ['admin-revenue-trend', months],
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await getSupabase().rpc('admin_get_revenue_trend', {
        p_admin_id: userId,
        p_months: months,
      });

      if (error) {
        console.error('Failed to fetch revenue trend:', error);
        throw new Error(error.message);
      }

      return data as RevenueTrend[];
    },
    enabled: isReady,
  });
}

/**
 * Hook to get subscription statistics (admin only)
 */
export function useAdminSubscriptionStats() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: ['admin-subscription-stats'],
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await getSupabase().rpc('admin_get_subscription_stats', {
        p_admin_id: userId,
      });

      if (error) {
        console.error('Failed to fetch subscription stats:', error);
        throw new Error(error.message);
      }

      return data as SubscriptionStats[];
    },
    enabled: isReady,
  });
}

/**
 * Hook to delete user (admin only)
 */
export function useDeleteUser() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (!user?.id) throw new Error('Not authenticated');

      // Rate limit check
      const rateLimit = checkRateLimit(`admin-delete-user-${user.id}`, 5, 60000);
      if (!rateLimit.allowed) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }

      const { data, error } = await getSupabase().rpc('admin_delete_user', {
        p_admin_id: user.id,
        p_user_id: userId,
      });

      if (error) {
        console.error('Failed to delete user:', error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-statistics'] });
    },
  });
}

/**
 * Hook to fetch subscribers whose subscription / beta period expires within
 * the next N days. Used by the admin dashboard "expiring soon" widget.
 *
 * Includes BOTH paid subscribers (current_period_end) and beta testers
 * (beta_expires_at) — the underlying RPC unions them with `expires_source`
 * marking which bucket each row came from.
 */
export function useExpiringSoon(days: number = 30) {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: ['admin-expiring-soon', days],
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await getSupabase().rpc('admin_get_expiring_soon', {
        p_admin_id: userId,
        p_days: days,
      });

      if (error) {
        console.error('Failed to fetch expiring-soon list:', error);
        throw new Error(error.message);
      }

      return (data ?? []) as ExpiringSoonRow[];
    },
    enabled: isReady && !!userId,
    staleTime: 60 * 1000, // refresh every minute
  });
}

/**
 * Hook to ask the admin-payment-link Edge Function to generate a Stripe
 * Customer Portal session (existing customers) or Checkout session (new
 * customers), and email the link to the target subscriber.
 *
 * Returns both the URL (so admin can copy/paste or show in a toast) and
 * a flag for whether the email was actually dispatched.
 */
export function useSendPaymentLink() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation<SendPaymentLinkResult, Error, SendPaymentLinkInput>({
    mutationFn: async (input) => {
      if (!user?.id) throw new Error('Not authenticated');

      const rateLimit = checkRateLimit(`admin-pay-link-${user.id}`, 20, 60000);
      if (!rateLimit.allowed) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }

      // Edge Function is invoked via supabase.functions.invoke which forwards
      // the Clerk JWT automatically (set up by our supabase client).
      const { data, error } = await getSupabase().functions.invoke('admin-payment-link', {
        body: input,
      });

      // Read the JSON body of a failed call — never "Edge Function returned a
      // non-2xx status code".
      const failure = await readFunctionError(data, error);
      if (failure || !data) {
        console.error('admin-payment-link failed:', failure ?? error);
        // .code lets the UI show a readable text (EA_SOLD_OUT, HAS_LIVE_SUBSCRIPTION…)
        throw new BillingError(failure?.code ?? 'SERVER', failure, failure?.message || 'Failed to generate payment link');
      }

      return data as SendPaymentLinkResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-subscribers'] });
    },
  });
}

// Export for convenience
export {
  verifyAdminServer,
};
